/**
 * ActionQueueProvider — persisted Action Queue store.
 *
 * In-memory for the current prototype, but the API is shaped to map cleanly
 * onto a future Lovable Cloud / Supabase table without refactoring callers.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type {
  ActionQueueItem,
  ActionQueueStatus,
  CreateActionQueueItemInput,
} from "./types";

let _aqCounter = 0;
function generateActionQueueId(): string {
  _aqCounter++;
  return `AQ-${Date.now().toString(36)}-${String(_aqCounter).padStart(3, "0")}`;
}

interface ActionQueueContextValue {
  items: ActionQueueItem[];
  /** Create a new persisted queue item. Returns the created record. */
  createItem: (input: CreateActionQueueItemInput) => ActionQueueItem;
  /** Update status with appropriate timestamps. */
  updateStatus: (id: string, status: ActionQueueStatus) => void;
  /** Mark an item complete. Convenience over updateStatus. */
  completeItem: (id: string, notes?: string) => void;
  /** Dismiss an item. Convenience over updateStatus. */
  dismissItem: (id: string, notes?: string) => void;
  /** Re-assign ownership. */
  assignItem: (id: string, assignment: {
    roleKey?: string;
    userId?: string;
    label?: string;
  }) => void;
  /** Lookup helper. */
  getItem: (id: string) => ActionQueueItem | undefined;
}

const ActionQueueContext = createContext<ActionQueueContextValue | undefined>(undefined);

export function ActionQueueProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ActionQueueItem[]>([]);

  const createItem = useCallback((input: CreateActionQueueItemInput): ActionQueueItem => {
    const now = new Date().toISOString();
    const item: ActionQueueItem = {
      id: generateActionQueueId(),
      title: input.title,
      summary: input.summary,
      status: "open",
      priority: input.priority ?? "medium",
      createdAt: now,
      assignedToRoleKey: input.assignedToRoleKey,
      assignedToUserId: input.assignedToUserId,
      assignedToLabel: input.assignedToLabel,
      actionKey: input.actionKey,
      actionLabel: input.actionLabel,
      actionPayload: input.actionPayload,
      intent: input.intent,
      sourceType: input.sourceType ?? "system",
      sourceId: input.sourceId,
      workflowSourceType: input.workflowSourceType ?? input.sourceType ?? "system",
      workflowSourceId: input.workflowSourceId ?? input.sourceId,
      originHandoffId: input.originHandoffId,
      linkedHandoffId: input.linkedHandoffId,
      jobId: input.jobId,
      issueId: input.issueId,
      estimateId: input.estimateId,
      actionId: input.actionId,
      createdBy: input.createdBy,
      notes: input.notes,
    };
    setItems((prev) => [item, ...prev]);
    return item;
  }, []);

  const updateStatus = useCallback((id: string, status: ActionQueueStatus) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const now = new Date().toISOString();
        const next: ActionQueueItem = { ...item, status };
        if (status === "in_progress" && !item.startedAt) next.startedAt = now;
        if (status === "completed") next.completedAt = now;
        if (status === "dismissed") next.dismissedAt = now;
        return next;
      }),
    );
  }, []);

  const completeItem = useCallback((id: string, notes?: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status: "completed",
              completedAt: new Date().toISOString(),
              notes: notes ?? item.notes,
            }
          : item,
      ),
    );
  }, []);

  const dismissItem = useCallback((id: string, notes?: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status: "dismissed",
              dismissedAt: new Date().toISOString(),
              notes: notes ?? item.notes,
            }
          : item,
      ),
    );
  }, []);

  const assignItem = useCallback(
    (id: string, assignment: { roleKey?: string; userId?: string; label?: string }) => {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                assignedToRoleKey: assignment.roleKey ?? item.assignedToRoleKey,
                assignedToUserId: assignment.userId ?? item.assignedToUserId,
                assignedToLabel: assignment.label ?? item.assignedToLabel,
              }
            : item,
        ),
      );
    },
    [],
  );

  const getItem = useCallback(
    (id: string) => items.find((i) => i.id === id),
    [items],
  );

  const value = useMemo<ActionQueueContextValue>(
    () => ({ items, createItem, updateStatus, completeItem, dismissItem, assignItem, getItem }),
    [items, createItem, updateStatus, completeItem, dismissItem, assignItem, getItem],
  );

  return <ActionQueueContext.Provider value={value}>{children}</ActionQueueContext.Provider>;
}

export function useActionQueueStore(): ActionQueueContextValue {
  const ctx = useContext(ActionQueueContext);
  if (!ctx) throw new Error("useActionQueueStore must be used within ActionQueueProvider");
  return ctx;
}
